import { PSI_PLATFORM_BE_URL, API_CORE_URL, API_CORE_CONFIG, PSYCHOLOGY_API_CORE_KEY } from '../config';

export interface ApiCoreLoginRequest {
  email: string;
  google_access_token: string;
}

export interface ApiCoreLoginResponse {
  success: true;
  data: {
    token: string;
    user: {
      id: string;
      email: string;
      name: string;
      surname: string;
    };
    scopes: string[];
  };
}

export interface ApiCoreLoginError {
  success: false;
  message?: string;
  error?: string;
  code?: string;
  status?: number;
}

// Tipo unión para la respuesta completa
export type ApiCoreLoginResult = ApiCoreLoginResponse | ApiCoreLoginError;

/**
 * Crear AbortController con timeout
 */
function createTimeoutController(timeoutMs: number): AbortController {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller;
}

/**
 * Traducir mensajes de error provenientes de API Core al español
 */
function translateApiErrorMessage(message: string, status?: number): string {
	const trimmed = message?.trim() ?? '';
	const lower = trimmed.toLowerCase();

	// Mapeos explícitos por contenido del mensaje
	if (lower.includes('user not found')) return 'Usuario no encontrado';
	if (lower.includes('email not found')) return 'Email no encontrado';
	if (lower.includes('invalid credentials')) return 'Datos de autenticación inválidos';
	if (lower.includes('unauthorized')) return 'Email no autorizado para acceder al sistema';
	if (lower.includes('forbidden')) return 'Acceso denegado. Verifica tus permisos';
	if (lower.includes('too many requests')) return 'Demasiados intentos. Intenta más tarde';

	// Fallback por código de estado HTTP
	switch (status) {
		case 400:
			return 'Datos de autenticación inválidos';
		case 401:
			return 'Email no autorizado para acceder al sistema';
		case 403:
			return 'Acceso denegado. Verifica tus permisos';
		case 404:
			return 'Usuario no encontrado';
		case 429:
			return 'Demasiados intentos. Intenta más tarde';
		case 500:
		case 502:
		case 503:
			return 'Error del servidor. Intenta más tarde';
		default:
			return trimmed || 'Ocurrió un error inesperado';
	}
}

/**
 * Realizar login con email en API Core
 * Mantiene simplicidad pero con mejor manejo de errores y robustez
 */
export async function loginToApiCore(email: string, googleAccessToken: string): Promise<ApiCoreLoginResponse> {
  const url = `${PSI_PLATFORM_BE_URL}/api/auth/login`;
  const controller = createTimeoutController(API_CORE_CONFIG.TIMEOUT);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': PSYCHOLOGY_API_CORE_KEY,
      },
      body: JSON.stringify({ email, google_access_token: googleAccessToken }),
      signal: controller.signal,
    });

    // Intentar parsear la respuesta JSON
    let data: unknown;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('❌ Error parseando respuesta JSON:', parseError);
      throw new Error(`Error del servidor: respuesta no válida (HTTP ${response.status})`);
    }

    // Manejo de errores HTTP
    if (!response.ok) {
      console.error(`❌ Error HTTP ${response.status}:`, data);
      
      // Priorizar el mensaje 'description' de la API, luego otros campos
      const errorData = data as Record<string, unknown>;
      const apiErrorMessage = errorData?.description || errorData?.message || errorData?.error;
      
      if (apiErrorMessage && typeof apiErrorMessage === 'string') {
			const translatedMessage = translateApiErrorMessage(apiErrorMessage, response.status);
			throw new Error(translatedMessage);
      }
      
      // Si no hay mensaje específico de la API, usar mensajes por defecto
      let userFriendlyMessage: string;
      switch (response.status) {
        case 400:
          userFriendlyMessage = 'Datos de autenticación inválidos';
          break;
        case 401:
          userFriendlyMessage = 'Email no autorizado para acceder al sistema';
          break;
        case 403:
          userFriendlyMessage = 'Acceso denegado. Verifica tus permisos';
          break;
        case 404:
          userFriendlyMessage = 'Email no encontrado en el sistema';
          break;
        case 429:
          userFriendlyMessage = 'Demasiados intentos. Intenta más tarde';
          break;
        case 500:
        case 502:
        case 503:
          userFriendlyMessage = 'Error del servidor. Intenta más tarde';
          break;
        default:
          userFriendlyMessage = `Error de conexión (${response.status})`;
      }
      
      throw new Error(userFriendlyMessage);
    }

    // Validar estructura de respuesta exitosa
    const responseData = data as Record<string, unknown>;
    if (responseData?.success === true && 
        responseData?.data && 
        typeof responseData.data === 'object' && 
        responseData.data !== null &&
        'token' in responseData.data) {
      return data as ApiCoreLoginResponse;
    } else {
      console.error('❌ Respuesta inválida del servidor:', data);
      throw new Error('El servidor no devolvió un token válido');
    }

  } catch (error) {
    // Manejo específico de errores de red
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        console.error('⏰ Timeout en autenticación con API Core');
        throw new Error('La autenticación está tardando demasiado. Verifica tu conexión');
      }
      
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        console.error('🌐 Error de red:', error.message);
        throw new Error('No se pudo conectar con el servidor. Verifica tu conexión a internet');
      }
    }

    console.error('❌ Error en login de API Core:', error);
    throw error;
  }
}

/**
 * Función de utilidad para probar la conexión con API Core
 * Útil para debugging y verificar configuración
 */
export async function testApiCoreConnection(): Promise<{
  success: boolean;
  url: string;
  message: string;
  responseTime?: number;
}> {
  const url = `${API_CORE_URL}/v1/psi-platform/auth/login`;
  const startTime = Date.now();
  
  try {
    const controller = createTimeoutController(5000); // 5 segundos para test
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': PSYCHOLOGY_API_CORE_KEY,
      },
      body: JSON.stringify({ email: 'test@connection.test' }),
      signal: controller.signal,
    });

    const responseTime = Date.now() - startTime;
    
    return {
      success: true,
      url,
      message: `Conexión exitosa (HTTP ${response.status}) - ${responseTime}ms`,
      responseTime
    };
  } catch (error) {
    const responseTime = Date.now() - startTime;
    let message = 'Error de conexión';
    
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        message = 'Timeout - El servidor no responde';
      } else if (error.message.includes('Failed to fetch')) {
        message = 'No se puede conectar al servidor';
      } else {
        message = error.message;
      }
    }
    
    return {
      success: false,
      url,
      message: `${message} - ${responseTime}ms`,
      responseTime
    };
  }
}
